'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cx } from '../../../internal/cx.js';
import { CloseGlyph } from '../../../internal/icons.js';
import { dismissHandlers } from '../../../internal/dismiss.js';
import { DialogBody, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '../dialog/index.js';
/**
 * A panel that slides in from an edge of the screen: detail views, filters,
 * longer forms and mobile navigation. It is a modal dialog (Radix Dialog), so
 * focus is trapped, the page behind does not scroll, Escape closes it and
 * focus returns to the trigger. Every sheet needs a SheetTitle.
 */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetContent = forwardRef(function SheetContent({ side = 'right', size = 'md', closeLabel = 'Close', footer, busy = false, closeOnOutsideClick = true, escapeGuard = true, handle, onEscapeKeyDown, onInteractOutside, className, children, ...rest }, ref) {
    const guards = dismissHandlers({ busy, closeOnOutsideClick, escapeGuard }, { onEscapeKeyDown, onInteractOutside });
    const showHandle = handle ?? side === 'bottom';
    return (_jsxs(DialogPrimitive.Portal, { children: [_jsx(DialogPrimitive.Overlay, { className: "mn-overlay" }), _jsxs(DialogPrimitive.Content, { ref: ref, ...rest, ...guards, "aria-busy": busy || rest['aria-busy'] || undefined, "data-side": side, "data-size": size, "data-busy": busy || undefined, className: cx('mn-sheet', className), children: [showHandle ? _jsx("div", { className: "mn-sheet__handle", "aria-hidden": true }) : null, children, footer ? _jsx(SheetFooter, { children: footer }) : null, closeLabel ? (_jsx(DialogPrimitive.Close, { className: "mn-dialog__close mn-sheet__close", "aria-label": closeLabel, disabled: busy || undefined, children: _jsx(CloseGlyph, {}) })) : null] })] }));
});
/** Title and description block at the top of the sheet. */
export function SheetHeader({ className, ...rest }) {
    return _jsx(DialogHeader, { ...rest, className: cx('mn-sheet__header', className) });
}
/** The sheet's accessible name. Required; wrap in VisuallyHidden to hide it. */
export const SheetTitle = forwardRef(function SheetTitle({ className, ...rest }, ref) {
    return _jsx(DialogTitle, { ref: ref, ...rest, className: cx('mn-sheet__title', className) });
});
/** Becomes the sheet's accessible description. */
export const SheetDescription = forwardRef(function SheetDescription({ className, ...rest }, ref) {
    return (_jsx(DialogDescription, { ref: ref, ...rest, className: cx('mn-sheet__description', className) }));
});
/** The scrolling region between header and footer. */
export function SheetBody({ className, ...rest }) {
    return _jsx(DialogBody, { ...rest, className: cx('mn-sheet__body', className) });
}
/** Sticky actions at the bottom of the sheet. */
export function SheetFooter({ className, ...rest }) {
    return _jsx(DialogFooter, { ...rest, className: cx('mn-sheet__footer', className) });
}
//# sourceMappingURL=sheet.js.map